import {
  Button,
  Divider,
  Menu,
  type FloatingCtrlRef,
  type OpenCtrlRef
} from "@theorgchartdev/storybook";
import cx from "classnames";
import ToolbarPopover from "@/appComponents/Navigation/ContextualToolbar/ToolbarPopover/ToolbarPopover";
import { useEscapePortalContainer } from "@/providers/EscapePortalContainer";
import { useTopic } from "@/hooks/useTopic";
import { Fragment, useEffect, useRef, useState, type KeyboardEvent } from "react";
import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { GetDoc, GetWSMgr } from "@/Utils/StateLib";
import { Globals } from "@coreapp/CoreApp/Globals";
import { appstr } from "@coreapp/utils/appstr";
import type { QChartBox } from "@coreapp/CoreApp/QChartBox";
import { GetToolbarTargetBox } from "@coreapp/utils/handleMouseUp";
import {
  getBoxToolbarModel,
  kEmptyBoxToolbarModel,
  type BoxToolbarModel
} from "../getBoxToolbarModel";
import { useToast } from "@/providers/ToastProvider";
import {
  handleAddBoxToLibrary,
  handleBoxFormatCommand,
  handleBranchStyleChange,
  handleCanvasClickCommand
} from "../utils";

type ClickToolbarPayload = { box: QChartBox; isPosition: boolean } | null;
type WSMgr = NonNullable<ReturnType<typeof GetWSMgr>>;
type CanvasPanel = NonNullable<WSMgr["fCanvasPanel"]>;

const kToolbarId = "ctb-chart-click-toolbar";

const getToolbarButtons = () =>
  Array.from(document.querySelectorAll<HTMLElement>(`#${kToolbarId} button:not([disabled])`));

const kFocusRetryFrames = 10;

const focusFirstToolbarButton = (framesLeft = kFocusRetryFrames) => {
  requestAnimationFrame(() => {
    const first = getToolbarButtons()[0];
    first?.focus();
    if (document.activeElement !== first && framesLeft > 0) focusFirstToolbarButton(framesLeft - 1);
  });
};

const focusCanvas = () => document.getElementById("basecanvas")?.focus();

const isLiveBox = (box: QChartBox | null): box is QChartBox => !!box && box.fClosed !== true;

const GetPadPosition = (targetBox: QChartBox, cp: CanvasPanel) => {
  if (!isLiveBox(targetBox)) return null;
  const canvas = document.getElementById("basecanvas");
  if (!canvas) return null;
  const canvasRect = canvas.getBoundingClientRect();

  const scaleFactor = cp.GetScaleFactor();
  const panX = cp.GetPanX();
  const panY = cp.GetPanY();
  const panelOffsetX = cp.GetPositionX();
  const panelOffsetY = cp.GetPositionY();

  const width = targetBox.GetWidth() * scaleFactor;

  const x = canvasRect.left + (targetBox.GetPositionX() - panX) * scaleFactor + panelOffsetX;
  const y = canvasRect.top + (targetBox.GetPositionY() - panY) * scaleFactor + panelOffsetY;

  const anchorX = x + width / 2;
  const anchorY = y;

  return {
    x: anchorX,
    y: anchorY,
    width: 0,
    height: 0,
    top: anchorY,
    left: anchorX,
    right: anchorX,
    bottom: anchorY
  };
};

export const ChartBoxContextToolbar = () => {
  const [activeChartBox, setActiveChartBox] = useState<QChartBox | null>(null);
  const [isPositionTarget, setIsPositionTarget] = useState(false);
  const openCtrlRef = useRef<OpenCtrlRef | null>(null);
  const floatingCtrlRef = useRef<FloatingCtrlRef | null>(null);
  const [overflowOpen, setOverflowOpen] = useState(false);
  const escapeContainer = useEscapePortalContainer();
  const portalFloatingProps = escapeContainer
    ? { portalProps: { container: escapeContainer } }
    : undefined;

  useEffect(
    () =>
      openCtrlRef.current?.subscribeOpen?.(open => {
        const cp = GetWSMgr()?.fCanvasPanel;
        if (cp) cp.fChartToolbarOpen = open;
      }),
    []
  );

  const {
    canAccessProfileCard,
    canEditDocuments,
    canAccessRecordDetails,
    canViewRecordSourceDetails,
    canUpdatePlan,
    canAccessBoxLibrary
  } = usePermissionCheck();
  const { addToast } = useToast() as unknown as {
    addToast: (toast: Record<string, unknown>) => void;
  };

  const repositionToolbar = (target: QChartBox, cp: CanvasPanel): boolean => {
    const anchor = GetPadPosition(target, cp);
    if (!anchor) return false;

    floatingCtrlRef.current?.floating.refs.setPositionReference({
      getBoundingClientRect() {
        return anchor;
      }
    });
    return true;
  };

  const buildToolbarModel = (target: QChartBox | null, isPosition: boolean): BoxToolbarModel => {
    const doc = GetDoc();
    if (!doc || !isLiveBox(target)) return kEmptyBoxToolbarModel;

    return getBoxToolbarModel({
      doc,
      targetBox: target,
      isPosition,
      uiMode: Globals._uiConfig.GetUIMode(),
      readOnly: Globals._accountInfo.ReadOnlyMode(),
      permissions: {
        canAccessProfileCard,
        canEditDocuments,
        canAccessRecordDetails,
        canViewRecordSourceDetails,
        canUpdatePlan,
        canAccessBoxLibrary
      },
      planMgr: GetWSMgr()?.GetPlanMgr(),
      onCommand: handleCanvasClickCommand(openCtrlRef),
      onBranchStyleChange: handleBranchStyleChange(openCtrlRef),
      onFormatCommand: handleBoxFormatCommand(openCtrlRef),
      onAddToLibrary: handleAddBoxToLibrary(openCtrlRef, addToast),
      wrapAction: doAction => () => {
        openCtrlRef.current?.setOpen(false);
        doAction();
      }
    });
  };

  const updateClickToolbar = (payload: ClickToolbarPayload) => {
    const target = payload?.box ?? null;
    const isPosition = !!payload?.isPosition;
    const cp = GetWSMgr()?.fCanvasPanel;

    setActiveChartBox(target);
    setIsPositionTarget(isPosition);

    const { row, overflow } = buildToolbarModel(target, isPosition);
    const isEmpty = row.length === 0 && overflow.length === 0;
    if (!target || isEmpty || !cp || !repositionToolbar(target, cp)) {
      openCtrlRef.current?.setOpen(false);
      return;
    }

    openCtrlRef.current?.setOpen(true);
  };

  useTopic("canvasClickToolbar", updateClickToolbar);

  useTopic("BoundaryChange", () => {
    if (!activeChartBox) return;
    const cp = GetWSMgr()?.fCanvasPanel;
    if (!cp || !repositionToolbar(activeChartBox, cp)) {
      openCtrlRef.current?.setOpen(false);
      setActiveChartBox(null);
    }
  });

  const closeToolbar = () => {
    setActiveChartBox(null);
    openCtrlRef.current?.setOpen(false);
  };

  useTopic("chartDocumentLoaded", closeToolbar);
  useTopic("subchartSwitch", closeToolbar);

  // Stay open only while the selection still resolves to the anchored box (the box or one of its
  // cells). Checked after the current change settles: a click deselects, then selects, synchronously.
  useTopic("CanvasSelectionChange", () => {
    if (!activeChartBox) return;
    queueMicrotask(() => {
      const cp = GetWSMgr()?.fCanvasPanel;
      if (!cp || GetToolbarTargetBox(cp) !== activeChartBox) closeToolbar();
    });
  });

  useTopic("canvasToolbarFocus", () => focusFirstToolbarButton());

  // Keyboard-triggered actions (incl. overflow items, whose keys bubble here) refocus their record.
  const rememberKeyboardAction = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!activeChartBox || (event.key !== "Enter" && event.key !== " ")) return;
    GetWSMgr()?.fCanvasPanel?.RememberKeyboardRefocus(activeChartBox);
  };

  const handleToolbarKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const buttons = getToolbarButtons();
    const index = buttons.indexOf(event.target as HTMLElement);
    if (index === -1) return;

    const focusAt = (next: number) => {
      event.preventDefault();
      buttons[(next + buttons.length) % buttons.length]?.focus();
    };

    switch (event.key) {
      case "ArrowRight":
        focusAt(index + 1);
        break;
      case "ArrowLeft":
        focusAt(index - 1);
        break;
      case "Home":
        focusAt(0);
        break;
      case "End":
        focusAt(buttons.length - 1);
        break;
      case "Escape":
      case "Tab":
        event.preventDefault();
        event.stopPropagation();
        closeToolbar();
        focusCanvas();
        break;
    }
  };

  const model = buildToolbarModel(activeChartBox, isPositionTarget);

  return (
    <ToolbarPopover
      openCtrlRef={openCtrlRef}
      otherPopoverProps={{
        reference: null,
        floatingCtrlRef,
        offsetOptions: 0,
        options: { placement: "bottom-start", strategy: "fixed" },
        floatingProps: portalFloatingProps,
        dismissOptions: { escapeKey: !overflowOpen },
        flipOptions: { mainAxis: false, crossAxis: false },
        shiftOptions: { mainAxis: false, crossAxis: false }
      }}
      overlayProps={{
        className: cx("oc-dynamic-list-size", "oc-chart-box-context-toolbar"),
        padding: 0,
        customStyle: { transform: "translate(-50%, calc(-100% - 6px))" }
      }}
    >
      <div
        className={"oc-chart-box-context-toolbar-row"}
        id={kToolbarId}
        role="toolbar"
        aria-orientation="horizontal"
        onKeyDown={handleToolbarKeyDown}
        onKeyDownCapture={rememberKeyboardAction}
      >
        {model.row.map(opt => (
          <Fragment key={opt.value}>
            <Button
              iconOnly
              size={"small"}
              modifier={"tertiary"}
              iconProps={{
                name: opt.icon,
                size: "medium-small",
                ...(opt.iconColor && { color: opt.iconColor })
              }}
              onClick={opt.clickHandler}
              disabled={opt.disabled}
              dataTestId={opt.dataTestId}
              ariaLabel={opt.label}
              tooltipProps={{ message: opt.label }}
            />
            {opt.divider && <Divider vertical />}
          </Fragment>
        ))}
        {model.overflow.length > 0 && (
          <Menu
            trigger={
              <Button
                iconOnly
                size={"small"}
                modifier={"tertiary"}
                iconProps={{ name: "ellipsis-vertical", size: "medium-small" }}
                dataTestId={"ctb-box-toolbar-overflow"}
                ariaLabel={appstr("More Options")}
              />
            }
            listWidth={"medium"}
            options={model.overflow}
            popoverProps={{
              floatingProps: portalFloatingProps,
              onOpen: () => setOverflowOpen(true),
              onClose: () => setOverflowOpen(false)
            }}
          />
        )}
      </div>
    </ToolbarPopover>
  );
};
